import { useEffect, useState } from 'react';
import { notification } from 'antd';
import moment from 'moment-timezone';
import { useParams } from 'react-router-dom';
import APIClient from '../api/api';
import { RenewalJobDetail } from '../api/models';
import { Callout, Column, DashboardCard, DataTable, DomainName, StatusTag } from '../components/ui';
import { PageHead } from '../layout';
import {
  actionUsedNote,
  formatCents,
  isTerminalJobStatus,
  itemStatusLabel,
  itemStatusTone,
  jobProgress,
  jobStatusTone,
} from '../components/BulkRenewal.model';

const DEFAULT_POLL_INTERVAL_MS = 10000;

const Stat = ({ label, value, tone }: { label: string; value: string; tone?: 'danger' }) => (
  <div className="rounded-md border border-border bg-bg px-3 py-2">
    <div className="text-[10px] uppercase tracking-wider text-text-3 font-semibold">{label}</div>
    <div className={`text-[15px] mt-0.5 ${tone === 'danger' ? 'text-danger' : 'text-text'}`}>{value}</div>
  </div>
);

export interface Props {
  /** Override for tests. */
  pollIntervalMs?: number;
  /** Override for tests, otherwise read from the route. */
  jobId?: number;
}

const RenewalDetail = ({ pollIntervalMs = DEFAULT_POLL_INTERVAL_MS, jobId }: Props) => {
  const params = useParams();
  const id = jobId ?? Number(params.id);
  const [detail, setDetail] = useState<RenewalJobDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    let timer: number | undefined;

    const tick = async () => {
      try {
        const next = await APIClient.getRenewalJob(id);
        if (cancelled) return;
        setDetail(next);
        setLoading(false);
        if (!isTerminalJobStatus(next.job.status)) {
          timer = window.setTimeout(tick, pollIntervalMs);
        }
      } catch (e: any) {
        if (cancelled) return;
        setLoading(false);
        notification.error({ message: e?.response?.data?.error || 'Could not load this renewal' });
      }
    };

    tick();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [id, pollIntervalMs]);

  const job = detail?.job;
  const currency = job?.currency ?? '';

  const columns: Column<NonNullable<RenewalJobDetail['items']>[number]>[] = [
    { key: 'domain', title: 'Domain', render: (item) => <DomainName name={item.domain} /> },
    {
      key: 'status',
      title: 'Status',
      render: (item) => <StatusTag tone={itemStatusTone(item.status)}>{itemStatusLabel(item.status)}</StatusTag>,
    },
    {
      key: 'charged',
      title: 'Charged',
      align: 'right',
      render: (item) => formatCents(item.chargedAmountCents, currency),
    },
    {
      key: 'expires',
      title: 'Now expires',
      render: (item) => (item.newExpiresAt ? moment(item.newExpiresAt).format('YYYY-MM-DD') : '—'),
    },
    {
      key: 'action',
      title: 'Action',
      render: (item) =>
        (item.actionUsed ?? item.action ?? 'renew') === 'reactivate' ? 'Reactivated' : 'Renewed',
    },
    { key: 'order', title: 'Order', render: (item) => item.orderId || '—' },
    {
      key: 'reason',
      title: 'Detail',
      render: (item) => actionUsedNote(item) ?? item.failureReason ?? '',
    },
  ];

  const progress = job ? jobProgress(job) : { done: 0, total: 0, pct: 0 };
  const needsChecking = (detail?.counts?.unknown ?? 0) > 0;

  return (
    <>
      <PageHead
        title={job ? `Renewal #${job.id}` : 'Renewal'}
        subtitle={job ? `${job.ncUsername} · ${job.years} year${job.years > 1 ? 's' : ''} · started by ${job.requestedBy}` : undefined}
        actions={job ? <StatusTag tone={jobStatusTone(job.status)}>{job.status}</StatusTag> : undefined}
      />

      {job && (
        <div className="px-4 pb-3">
          <DashboardCard
            title={
              <div className="flex items-baseline gap-2 w-full">
                <h3 className="text-base font-semibold text-text m-0">Progress</h3>
                <span className="text-[12px] text-text-3">
                  {progress.done} of {progress.total} domains
                </span>
              </div>
            }
          >
            <div className="h-[6px] rounded-full bg-bg overflow-hidden mb-3">
              <div
                className={`h-full rounded-full ${job.failed > 0 ? 'bg-warn' : 'bg-accent'}`}
                style={{ width: `${progress.pct}%` }}
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Stat label="Renewed" value={String(job.succeeded)} />
              <Stat label="Failed" value={String(job.failed)} tone={job.failed > 0 ? 'danger' : undefined} />
              {job.reactivateCount > 0 && (
                <Stat label="Reactivations" value={String(job.reactivateCount)} />
              )}
              <Stat label="Charged" value={formatCents(job.chargedTotalCents, currency)} />
              <Stat label="Estimated" value={formatCents(job.estimatedTotalCents, currency)} />
            </div>
          </DashboardCard>
        </div>
      )}

      {needsChecking && (
        <div className="px-4 pb-3">
          <Callout tone="warn">
            One or more domains gave no answer, so we cannot tell whether they were charged. Check those at the
            registrar before running them again, particularly any reactivation: a second restore costs many times
            what a second renewal does.
          </Callout>
        </div>
      )}

      {job?.status === 'halted' && (
        <div className="px-4 pb-3">
          <Callout tone="warn">
            This run stopped early{job.failureReason ? `: ${job.failureReason}` : '.'} The domains it never reached
            were not renewed and can be run again.
          </Callout>
        </div>
      )}

      <DataTable
        columns={columns}
        data={detail?.items ?? []}
        rowKey={(item) => String(item.id)}
        loading={loading}
        emptyMessage="No domains in this renewal."
      />
    </>
  );
};

export default RenewalDetail;
